import * as React from "react";

import {
  calculateVisibleCount,
  getGapValue,
  getIntrinsicMeasuredWidth,
  getMeasuredWidth,
  isHTMLElement,
  MIN_TRUSTED_TAB_WIDTH,
  partitionVisibleValues,
  seedWidthMap,
  updateWidthMap,
} from "./measurement";
import type { RenderedTab, TabLocation, TabListLayoutContextValue, TabRenderMode } from "./context";

/** A tab's own width, or null when it has not been laid out and the number cannot be trusted. */
function getTabWidth(tab: RenderedTab): number | null {
  const width = tab.width || getMeasuredWidth(tab.root);
  return width > MIN_TRUSTED_TAB_WIDTH ? width : null;
}

/**
 * How much room the tab strip actually has.
 *
 * NOT the strip's own width — the strip is a flex child that has already been squeezed by its
 * siblings, so measuring it answers "how wide am I now", when the question is "how wide could I
 * be". This measures the parent and subtracts what the siblings take, skipping `display: none`
 * ones because they occupy nothing.
 */
function getAvailableWidth(element: HTMLElement): number {
  const parent = element.parentElement;
  if (!parent) return getMeasuredWidth(element);
  const parentWidth = getMeasuredWidth(parent);
  const parentGap = getGapValue(window.getComputedStyle(parent));
  const siblings = Array.from(parent.children).filter(
    (child) =>
      isHTMLElement(child) && child !== element && window.getComputedStyle(child).display !== "none",
  ) as HTMLElement[];
  const siblingWidth = siblings.reduce((total, sibling) => total + getMeasuredWidth(sibling), 0);
  return Math.max(0, parentWidth - siblingWidth - siblings.length * parentGap);
}

/**
 * Decide which tabs fit and which go into the overflow menu.
 *
 * ── `isMeasuring` IS A STATE, NOT A FLAG ──────────────────────────────────────────────────
 *
 * Measurement is not something that happens once. A container resize, a font load, a label
 * change, the overflow button appearing — each invalidates the answer. `isMeasuring` marks the
 * current layout as stale, and the effect below recomputes it. The list uses it to suppress
 * `data-ismeasuring` paint so the user never sees the intermediate arrangement.
 *
 * ── THE RETRY IS CAPPED AT FIVE FRAMES ────────────────────────────────────────────────────
 *
 * When a width is unknown the answer is `null`, and the honest response is to wait a frame and
 * ask again. Uncapped, a tab strip inside a `display: none` ancestor would retry forever at 60
 * frames a second. Five is enough for fonts and layout to settle and cheap enough to be
 * invisible if they never do.
 */
export function useOverflow({
  container,
  menuOpen,
  overflowButton,
  selected,
  tabs,
}: {
  container: React.RefObject<HTMLElement | null>;
  menuOpen: boolean;
  overflowButton: React.RefObject<HTMLElement | null>;
  selected: string | undefined;
  tabs: RenderedTab[];
}): [string[], string[], boolean] {
  const orderedValues = React.useMemo(() => tabs.map((tab) => tab.value), [tabs]);

  /*
   * A key over every tab's value AND width. Comparing the tabs array by identity would re-measure
   * on every render; comparing only values would miss a label change that made a tab wider.
   */
  const measurementInputKey = React.useMemo(
    () => tabs.map((tab) => `${tab.value}:${tab.width.toFixed(2)}`).join("\0"),
    [tabs],
  );

  const [visibleCount, setVisibleCount] = React.useState(0);
  const [isMeasuring, setIsMeasuring] = React.useState(true);
  const [measureRetryVersion, setMeasureRetryVersion] = React.useState(0);

  const pinnedSelectionRef = React.useRef(selected);
  const previousOverflowButtonWidthRef = React.useRef(0);
  const previousMeasurementInputKeyRef = React.useRef(measurementInputKey);
  const previousMenuOpenRef = React.useRef(menuOpen);
  const measureRetryFrameRef = React.useRef<number | null>(null);
  const measureRetryCountRef = React.useRef(0);

  const baseHiddenValues = orderedValues.slice(visibleCount);
  const selectedIsHidden = selected !== undefined && baseHiddenValues.includes(selected);
  /*
   * The pin STICKS after the selected tab has been pulled into view. Recomputing it from
   * "is the selection currently hidden" would drop the pin the moment it succeeded, and the tab
   * would fall straight back into the menu — a flicker loop.
   */
  const pinnedValue = selectedIsHidden ? selected : pinnedSelectionRef.current;

  const getCurrentPinnedValue = React.useCallback(
    () => (selectedIsHidden ? selected : pinnedSelectionRef.current),
    [selected, selectedIsHidden],
  );

  const markMeasurementStale = React.useCallback(() => setIsMeasuring(true), []);

  const measureVisibleCount = React.useCallback(
    (pinned: string | null | undefined) => {
      const element = container.current;
      if (!element) return null;
      const maxWidth = getAvailableWidth(element);
      const gap = getGapValue(window.getComputedStyle(element));
      const overflowWidth = overflowButton.current
        ? overflowButton.current.offsetWidth + gap
        : 0;
      return calculateVisibleCount({
        gap,
        maxWidth,
        overflowWidth,
        pinnedValue: pinned,
        tabs: tabs.map((tab) => ({ value: tab.value, width: getTabWidth(tab) })),
      });
    },
    [container, overflowButton, tabs],
  );

  const clearMeasureRetry = React.useCallback(() => {
    if (measureRetryFrameRef.current != null) cancelAnimationFrame(measureRetryFrameRef.current);
    measureRetryFrameRef.current = null;
    measureRetryCountRef.current = 0;
  }, []);

  React.useEffect(() => clearMeasureRetry, [clearMeasureRetry]);

  // Pull the selected tab into view the moment it becomes hidden.
  React.useLayoutEffect(() => {
    if (selected === undefined || !selectedIsHidden) return;
    pinnedSelectionRef.current = selected;
    const nextVisibleCount = measureVisibleCount(selected);
    if (nextVisibleCount == null) {
      markMeasurementStale();
      return;
    }
    if (nextVisibleCount !== visibleCount) setVisibleCount(nextVisibleCount);
    if (isMeasuring) setIsMeasuring(false);
  }, [isMeasuring, markMeasurementStale, measureVisibleCount, selected, selectedIsHidden, visibleCount]);

  // Re-measure when the strip, its parent or any sibling changes width.
  React.useEffect(() => {
    const element = container.current;
    // Not while the menu is open: moving tabs out from under an open menu is disorienting, and
    // the menu's own presence changes the widths being measured.
    if (!element || menuOpen || isMeasuring || typeof ResizeObserver === "undefined") return;

    const observed: HTMLElement[] = [element];
    const parent = element.parentElement;
    if (parent) {
      observed.push(parent);
      for (const child of Array.from(parent.children)) {
        if (isHTMLElement(child) && child !== element) observed.push(child);
      }
    }
    const widths = seedWidthMap(observed);
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (!isHTMLElement(entry.target)) continue;
        if (updateWidthMap(widths, entry.target, entry.contentRect.width)) {
          // A width moved, but that only matters if it changes the ANSWER. Re-running layout for
          // a resize that leaves the same tabs visible is churn the user can see as a flicker.
          const next = measureVisibleCount(getCurrentPinnedValue());
          if (next != null && next === visibleCount) continue;
          markMeasurementStale();
          return;
        }
      }
    });
    for (const target of observed) observer.observe(target);
    return () => observer.disconnect();
  }, [container, getCurrentPinnedValue, isMeasuring, markMeasurementStale, measureVisibleCount, menuOpen, visibleCount]);

  // Closing the menu returns tabs to the strip, so the layout has to be recomputed.
  React.useLayoutEffect(() => {
    if (previousMenuOpenRef.current && !menuOpen) markMeasurementStale();
    previousMenuOpenRef.current = menuOpen;
  }, [markMeasurementStale, menuOpen]);

  /*
   * The overflow button's own width feeds the calculation, and the button only exists once
   * something overflows — so its appearance changes the answer that produced it. Re-measuring
   * only when tabs are already hidden avoids an infinite loop between the two.
   */
  React.useLayoutEffect(() => {
    const nextWidth = overflowButton.current?.offsetWidth ?? 0;
    if (previousOverflowButtonWidthRef.current === nextWidth) return;
    previousOverflowButtonWidthRef.current = nextWidth;
    if (visibleCount < tabs.length) markMeasurementStale();
  });

  React.useLayoutEffect(() => {
    if (previousMeasurementInputKeyRef.current !== measurementInputKey) {
      previousMeasurementInputKeyRef.current = measurementInputKey;
      markMeasurementStale();
    }
  }, [markMeasurementStale, measurementInputKey]);

  React.useLayoutEffect(() => {
    if (!isMeasuring || menuOpen) return;
    const next = measureVisibleCount(getCurrentPinnedValue());
    if (next == null) {
      if (measureRetryFrameRef.current != null) return;
      const element = container.current;
      // A strip with no width of its own is hidden, not slow. Retrying would spin for as long as
      // it stays hidden, so the counter resets and waits for it to appear instead.
      if (!element || getMeasuredWidth(element) <= MIN_TRUSTED_TAB_WIDTH) {
        measureRetryCountRef.current = 0;
        return;
      }
      if (measureRetryCountRef.current >= 5) return;
      measureRetryCountRef.current += 1;
      measureRetryFrameRef.current = requestAnimationFrame(() => {
        measureRetryFrameRef.current = null;
        setMeasureRetryVersion((version) => version + 1);
      });
      return;
    }
    clearMeasureRetry();
    setVisibleCount(next);
    setIsMeasuring(false);
  }, [clearMeasureRetry, getCurrentPinnedValue, isMeasuring, measureRetryVersion, measureVisibleCount, menuOpen, container]);

  const { visibleValues, hiddenValues } = partitionVisibleValues(
    orderedValues,
    visibleCount,
    pinnedValue,
  );
  return [visibleValues, hiddenValues, isMeasuring];
}

/**
 * Which tab the overflow menu is highlighting, and where each tab currently lives.
 *
 * The active value is REQUESTED rather than set: the menu may reopen with a different set of
 * hidden tabs, so a remembered value that is no longer hidden falls back to the first one
 * instead of pointing at a tab that is not in the menu.
 */
export function useOverflowLayoutState({
  hiddenValues,
  menuOpen,
  overflowMenuOpen,
  visibleValues,
}: {
  hiddenValues: string[];
  menuOpen: boolean;
  overflowMenuOpen: boolean;
  visibleValues: string[];
}) {
  const [requested, setRequested] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!overflowMenuOpen) setRequested(null);
  }, [overflowMenuOpen]);

  const resolvedOverflowActiveValue = React.useMemo(() => {
    if (!overflowMenuOpen) return null;
    if (requested && hiddenValues.includes(requested)) return requested;
    return hiddenValues[0] ?? null;
  }, [hiddenValues, overflowMenuOpen, requested]);

  const hiddenSet = React.useMemo(() => new Set(hiddenValues), [hiddenValues]);
  const visibleSet = React.useMemo(() => new Set(visibleValues), [visibleValues]);

  const getLocation = React.useCallback(
    (value: string): TabLocation => {
      if (visibleSet.has(value)) return "main";
      // Hidden tabs are only "in the overflow" while the menu is OPEN. With it closed they are
      // rendered off-screen for measurement, and a tab measured off-screen must not be a tab stop.
      if (menuOpen && hiddenSet.has(value)) return "overflow";
      return "hidden";
    },
    [hiddenSet, menuOpen, visibleSet],
  );

  const moveOverflowFocus = React.useCallback(
    (key: string, value: string) => {
      if (hiddenValues.length < 1) return false;
      const currentIndex = hiddenValues.indexOf(value);
      const fallbackIndex = resolvedOverflowActiveValue
        ? hiddenValues.indexOf(resolvedOverflowActiveValue)
        : 0;
      const startIndex = currentIndex >= 0 ? currentIndex : Math.max(0, fallbackIndex);
      const lastIndex = hiddenValues.length - 1;
      let nextIndex = startIndex;
      switch (key) {
        // Wraps at both ends: a menu is a loop, and stopping dead at the last item leaves a
        // keyboard user pressing a key that does nothing.
        case "ArrowDown":
          nextIndex = startIndex >= lastIndex ? 0 : startIndex + 1;
          break;
        case "ArrowUp":
          nextIndex = startIndex <= 0 ? lastIndex : startIndex - 1;
          break;
        case "Home":
          nextIndex = 0;
          break;
        case "End":
          nextIndex = lastIndex;
          break;
      }
      const next = hiddenValues[nextIndex];
      if (!next) return false;
      setRequested(next);
      return true;
    },
    [hiddenValues, resolvedOverflowActiveValue],
  );

  const tabListLayoutContext = React.useMemo<TabListLayoutContextValue>(
    () => ({
      getLocation,
      moveOverflowFocus,
      overflowActiveValue: resolvedOverflowActiveValue,
      setOverflowActiveValue: setRequested,
    }),
    [getLocation, moveOverflowFocus, resolvedOverflowActiveValue],
  );

  return { resolvedOverflowActiveValue, tabListLayoutContext };
}

/**
 * Selecting from the overflow menu is a TWO-STEP commit.
 *
 * Choosing a tab inside the menu has to close the menu AND change the selection, and doing both
 * at once destroys the element that is being clicked while its own event is still running.
 * So the selection is parked, the menu closes, and the commit happens on the next layout — by
 * which time the tab has moved back into the strip and there is something real to focus.
 */
export function useOverflowSelectionState({
  commitSelection,
  menuOpen,
  setMenuOpen,
}: {
  commitSelection: (event: React.SyntheticEvent | null, value: string) => void;
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
}) {
  const selectionFromOverflowValueRef = React.useRef<string | null>(null);
  const pendingRef = React.useRef<{ event: React.SyntheticEvent | null; value: string } | null>(null);

  const setSelected = React.useCallback(
    (event: React.SyntheticEvent | null, value: string, source: "main" | "overflow" = "main") => {
      const fromOverflow = source === "overflow";
      selectionFromOverflowValueRef.current = fromOverflow ? value : null;
      if (fromOverflow) {
        // Persisted because the commit happens after this handler returns, by which point React
        // has recycled the event and every field on it reads as null.
        event?.persist();
        pendingRef.current = { event, value };
        setMenuOpen(false);
        return;
      }
      pendingRef.current = null;
      setMenuOpen(false);
      commitSelection(event, value);
    },
    [commitSelection, setMenuOpen],
  );

  React.useLayoutEffect(() => {
    if (menuOpen) return;
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    commitSelection(pending.event, pending.value);
  }, [commitSelection, menuOpen]);

  return { selectionFromOverflowValueRef, setSelected };
}

/**
 * Keep a tab's recorded width current as its content changes.
 *
 * ── WHY IT REFUSES TO MEASURE IN TWO PLACES ───────────────────────────────────────────────
 *
 * The same tab is also rendered inside the overflow menu and inside the off-screen measuring
 * container, where it is laid out under completely different constraints — a menu row is as wide
 * as the menu. Recording THAT as the tab's width would make it look enormous to the strip's
 * layout and evict its neighbours. Only a measurement taken in the strip counts.
 */
export function useRenderedTabWidth({
  hostElement,
  renderMode,
  tabRootRef,
  updateRenderedTab,
  value,
}: {
  hostElement: HTMLElement | null;
  renderMode: TabRenderMode;
  tabRootRef: React.RefObject<HTMLElement | null>;
  updateRenderedTab: (value: string, updates: { width: number }) => void;
  value: string;
}) {
  React.useEffect(() => {
    if (!hostElement) return;
    const element = tabRootRef.current;
    if (!element || typeof ResizeObserver === "undefined") return;

    const isSecondaryContext = () =>
      element.closest(".saltTabOverflowList-list") || element.closest(".saltTabList-measureContainer");

    const updateWidth = (allowSecondary = false) => {
      if (!element.isConnected) return;
      if (!allowSecondary && isSecondaryContext()) return;
      const width = getIntrinsicMeasuredWidth(element);
      // Zero means not laid out. Recording it would tell the layout this tab takes no room.
      if (width <= MIN_TRUSTED_TAB_WIDTH) return;
      updateRenderedTab(value, { width });
    };

    let frame: number | null = null;
    const schedule = (allowSecondary = false) => {
      if (typeof requestAnimationFrame !== "function") {
        updateWidth(allowSecondary);
        return;
      }
      if (frame != null) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = null;
        updateWidth(allowSecondary);
      });
    };

    // The first measurement is allowed from anywhere: without one the tab has no width at all,
    // and a wrong width beats none — none blocks the switch to portal mode forever.
    if (renderMode === "portal") schedule(true);
    else updateWidth(true);

    const resizeObserver = new ResizeObserver(() => updateWidth());
    resizeObserver.observe(element);
    const mutationObserver =
      typeof MutationObserver !== "undefined" ? new MutationObserver(() => schedule()) : null;
    // Text changes too, not just children: a label going from "Tab" to "Tab (12)" is a width
    // change no resize observer reports, because the box has not been re-laid-out yet.
    mutationObserver?.observe(element, { characterData: true, childList: true, subtree: true });

    return () => {
      if (frame != null) cancelAnimationFrame(frame);
      mutationObserver?.disconnect();
      resizeObserver.disconnect();
    };
  }, [hostElement, renderMode, tabRootRef, updateRenderedTab, value]);
}
